/**
 * Agent Routes Service
 * CRUD operations for daily visitation routes stored in Users/{uid}/Routes/{date}
 */
import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore'
import { getFirebaseDb } from '../firebase/firebase.js'
import logger from './logger.js'

const log = logger.createScoped('AgentRoutesService')

/**
 * Format a Date object as YYYY-MM-DD string
 * @param {Date} d
 * @returns {string}
 */
function formatDateStr(d) {
  const year = d.getFullYear()
  const month = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

/**
 * Get today's date as YYYY-MM-DD string
 * @returns {string}
 */
export function getTodayDateStr() {
  return formatDateStr(new Date())
}

/**
 * Get the nearest upcoming (or today) YYYY-MM-DD for a given weekday index (0=Sun … 4=Thu).
 * E.g. on Saturday 21/02 selecting Sunday (0) → '2026-02-22' (tomorrow).
 * @param {number} dayIndex - 0=Sunday, 1=Monday … 4=Thursday
 * @returns {string} YYYY-MM-DD
 */
export function getDateStrForWeekday(dayIndex) {
  const now = new Date()
  const todayDow = now.getDay()
  const daysAhead = (dayIndex - todayDow + 7) % 7
  const target = new Date(now)
  target.setDate(target.getDate() + daysAhead)
  return formatDateStr(target)
}

/**
 * Load a saved route for an agent on a specific date
 * @param {string} uid - Agent's Firebase UID
 * @param {string} [dateStr] - YYYY-MM-DD (defaults to today)
 * @returns {Promise<Array<{makat: string, name: string, city: string}>>} Ordered clients array
 */
export async function loadTodayRoute(uid, dateStr) {
  if (!uid) throw new Error('uid is required')

  const date = dateStr || getTodayDateStr()
  const db = getFirebaseDb()
  const routeRef = doc(db, 'Users', uid, 'Routes', date)

  try {
    const snap = await getDoc(routeRef)
    if (!snap.exists()) {
      log.debug('No saved route', { uid, date })
      return []
    }

    const data = snap.data()
    log.debug('Loaded route', { uid, date, count: data.clients?.length ?? 0 })
    return data.clients || []
  } catch (err) {
    log.error('Failed to load route', { uid, date, error: err })
    throw err
  }
}

/**
 * Save a route for an agent on a specific date
 * @param {string} uid - Agent's Firebase UID
 * @param {number} agentCode - Agent number
 * @param {Array<{makat: string, name: string, city: string}>} clients - Ordered clients array
 * @param {string} [dateStr] - YYYY-MM-DD (defaults to today)
 */
export async function saveTodayRoute(uid, agentCode, clients, dateStr) {
  if (!uid) throw new Error('uid is required')

  const date = dateStr || getTodayDateStr()
  const db = getFirebaseDb()
  const routeRef = doc(db, 'Users', uid, 'Routes', date)

  try {
    await setDoc(
      routeRef,
      {
        agentCode,
        clients,
        updatedAt: serverTimestamp(),
      },
      { merge: true }
    )

    log.debug('Saved route', { uid, date, count: clients.length })
  } catch (err) {
    log.error('Failed to save route', { uid, date, error: err })
    throw err
  }
}
